import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Check, Hourglass } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Card, EstadoVacio, Seccion } from '@/components/Card';
import { Segmentado } from '@/components/Segmentado';
import {
  confirmarAyuno,
  corregirAyuno,
  corregirInicioEnCurso,
  elegirMetodoDeAyuno,
  iniciarAyuno,
  leerAyunosRecientes,
  marcarAvisoLargoVisto,
  resolverCambioDeAyuno,
  terminarAyuno,
  type HorasDeAyuno,
  cerrarVentanaDeAlimentacion,
} from '@/db/ayunos';
import { mensajeDeError } from '@/db/tx';
import type { Fast, MetodoAyuno } from '@/db/types';
import { sumarDias, type FechaLocal } from '@/domain/dates';
import { fechaCorta } from '@/domain/hoy';
import { formatearNumero } from '@/domain/numbers';
import { fechaLocalDe, formatearHoraDeInstante, horaLocalDe, type HoraLocal } from '@/domain/time';
import {
  TEXTO_MOTIVO,
  textoAvisoLargo,
  textoHoras,
  textoPeriodo,
  textoRacha,
  type AvisoDeAyunoLargo,
  type DiaDeAyuno,
  type EstadoAyuno,
  type Periodo,
  type VentanaDeAlimentacion,
} from '@/domain/ayuno';
import { useFechaHoy } from '@/lib/useFechaHoy';
import { CampoHora, Opciones } from './comunes';

/*
 * Ayuno: lo que se comparte entre Hoy y el historial (Req. 23.8, 23.11 a
 * 23.13). Un ayuno calculado no cuenta hasta confirmarlo, y nada se cambia
 * sin que la persona lo decida.
 */

export const ORIGEN_AYUNO: Readonly<Record<Fast['origen'], string>> = {
  comidas: 'Confirmado por ti, calculado con tus comidas.',
  correccion: 'Con las horas que anotaste tú.',
  manual: 'Registrado a mano.',
  temporizador: 'Con el temporizador.',
};

/** El periodo que se muestra de un estado, para prellenar la corrección. */
export function periodoDelEstado(estado: EstadoAyuno): Periodo | null {
  if (estado.tipo === 'registrado' || estado.tipo === 'por_confirmar') return estado.periodo;
  if (estado.tipo === 'cambio') return estado.confirmado;
  return null;
}

/** Lo que se dice del ayuno de un día, con su estado (design.md, 8.10). */
export function DetalleDeAyuno({ estado }: { estado: EstadoAyuno }) {
  if (estado.tipo === 'registrado') {
    return (
      <>
        {/* textoPeriodo acaba en "a. m." o "p. m.", que ya traen su punto */}
        <p className="text-[13px] leading-relaxed text-texto">
          Ayuno de anoche: <span className="font-medium">{textoHoras(estado.horas)}</span>, {textoPeriodo(estado.periodo)}
          {estado.cumplio === true && ' Cumpliste tu meta.'}
          {estado.cumplio === false && ' Quedó por debajo de tu meta.'}
        </p>
        <p className="text-[12px] text-texto-suave">{ORIGEN_AYUNO[estado.ayuno.origen]}</p>
      </>
    );
  }
  if (estado.tipo === 'por_confirmar') {
    return (
      <>
        <p className="text-[13px] leading-relaxed text-texto">
          Ayuno de anoche: <span className="font-medium">{textoHoras(estado.horas)}</span>, {textoPeriodo(estado.periodo)}
        </p>
        <p className="text-[12px] leading-relaxed text-texto-suave">
          Calculado con tus comidas registradas. Queda por confirmar: hasta entonces no cuenta como dato.
        </p>
        {estado.dudaDeCena && (
          <p className="text-[12px] leading-relaxed text-aviso">Es bastante más que tu meta. ¿Faltó registrar tu cena de ayer?</p>
        )}
      </>
    );
  }
  if (estado.tipo === 'cambio') {
    return (
      <>
        <p className="text-[13px] leading-relaxed text-texto">
          Cambiaron las comidas de ese ayuno. Lo que confirmaste: <span className="font-medium">{textoHoras(estado.horasConfirmadas)}</span>,{' '}
          {textoPeriodo(estado.confirmado)} Con tus comidas de ahora: <span className="font-medium">{textoHoras(estado.horasNuevas)}</span>,{' '}
          {textoPeriodo(estado.nuevo)}
        </p>
        <p className="text-[12px] text-texto-suave">Nada cambia hasta que elijas cuál conservar.</p>
      </>
    );
  }
  if (estado.tipo === 'datos_insuficientes') {
    return (
      <>
        <p className="text-[13px] leading-relaxed text-texto">No hay registros suficientes para calcular tu ayuno de anoche.</p>
        <p className="text-[12px] leading-relaxed text-texto-suave">{TEXTO_MOTIVO[estado.motivo]}</p>
      </>
    );
  }
  return (
    <p className="text-[13px] leading-relaxed text-texto-suave">
      Tu ayuno de anoche aparecerá en cuanto registres tu primer alimento de hoy.
    </p>
  );
}

/**
 * Confirmar, corregir o resolver un cambio (Req. 23.11, 23.13). `etiquetaDia`
 * distingue los botones cuando hay varios días en la misma pantalla.
 */
export function AccionesDeAyuno({ dia, estado, etiquetaDia }: { dia: FechaLocal; estado: EstadoAyuno; etiquetaDia?: string }) {
  const [corrigiendo, setCorrigiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const de = etiquetaDia === undefined ? '' : ` el ${etiquetaDia}`;

  async function hacer(fn: () => Promise<unknown>) {
    if (guardando) return;
    setError(null);
    setGuardando(true);
    try {
      await fn();
      setCorrigiendo(false);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  if (estado.tipo === 'sin_ayuno') return null;

  return (
    <>
      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo">
          {error}
        </Banner>
      )}
      {corrigiendo ? (
        <FormularioAyuno
          dia={dia}
          inicial={periodoDelEstado(estado)}
          guardando={guardando}
          onGuardar={(horas) => void hacer(() => corregirAyuno(dia, horas))}
          onCancelar={() => setCorrigiendo(false)}
        />
      ) : (
        <div className="grid gap-2">
          {estado.tipo === 'por_confirmar' && (
            <Button icono={Check} disabled={guardando} onClick={() => void hacer(() => confirmarAyuno(dia))}>
              {guardando ? 'Guardando…' : etiquetaDia === undefined ? 'Confirmar este ayuno' : `Confirmar${de}`}
            </Button>
          )}
          {estado.tipo === 'cambio' && (
            <>
              <Button disabled={guardando} onClick={() => void hacer(() => resolverCambioDeAyuno(dia, 'nuevo'))}>
                Usar el nuevo{de}
              </Button>
              <Button variante="secundario" disabled={guardando} onClick={() => void hacer(() => resolverCambioDeAyuno(dia, 'confirmado'))}>
                Conservar el confirmado{de}
              </Button>
            </>
          )}
          <Button variante={estado.tipo === 'datos_insuficientes' ? 'secundario' : 'fantasma'} onClick={() => setCorrigiendo(true)}>
            {estado.tipo === 'datos_insuficientes' ? `Anotar las horas a mano${de}` : `Corregir las horas${de}`}
          </Button>
        </div>
      )}
    </>
  );
}

/**
 * Las horas de un ayuno a mano (Req. 23.4, 23.11). El fin es del día del
 * ayuno; el inicio, de ese día o del anterior, que es el caso normal.
 */
export function FormularioAyuno({
  dia,
  inicial,
  guardando,
  onGuardar,
  onCancelar,
}: {
  dia: FechaLocal;
  inicial: Periodo | null;
  guardando: boolean;
  onGuardar: (horas: HorasDeAyuno) => void;
  onCancelar: () => void;
}) {
  const ayer = sumarDias(dia, -1);
  const [empezoAyer, setEmpezoAyer] = useState(inicial === null ? true : fechaLocalDe(inicial.inicio) === ayer);
  const [horaInicio, setHoraInicio] = useState(inicial === null ? '20:00' : horaLocalDe(inicial.inicio));
  const [horaFin, setHoraFin] = useState(inicial === null ? '08:00' : horaLocalDe(inicial.fin));

  return (
    <div className="space-y-4 border-t border-borde pt-3">
      <Segmentado
        etiqueta="Empezó"
        etiquetaVisible
        completo
        valor={empezoAyer ? 'ayer' : 'hoy'}
        opciones={[
          { valor: 'ayer', etiqueta: 'Ayer' },
          { valor: 'hoy', etiqueta: 'Hoy' },
        ]}
        onCambio={(v) => setEmpezoAyer(v === 'ayer')}
      />
      <CampoHora etiqueta="Hora de inicio" valor={horaInicio} onCambio={setHoraInicio} />
      <CampoHora etiqueta="Hora de fin" valor={horaFin} onCambio={setHoraFin} ayuda={`Del ${fechaCorta(dia).toLocaleLowerCase('es-MX')}.`} />
      <div className="grid grid-cols-2 gap-2">
        <Button
          disabled={guardando}
          onClick={() =>
            onGuardar({
              fechaInicio: empezoAyer ? ayer : dia,
              horaInicio: horaInicio as HoraLocal,
              fechaFin: dia,
              horaFin: horaFin as HoraLocal,
            })
          }
        >
          {guardando ? 'Guardando…' : 'Guardar las horas'}
        </Button>
        <Button variante="secundario" onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Historial de ayunos (Req. 23.8, 23.12)
// ---------------------------------------------------------------------------

const DIAS_DEL_HISTORIAL = 30;

/** "12 h 05 min" o "—" */
const horasOGuion = (horas: number | null) => (horas === null ? '—' : textoHoras(horas));

export function AyunosPage() {
  const hoy = useFechaHoy();
  const datos = useLiveQuery(() => leerAyunosRecientes(hoy, DIAS_DEL_HISTORIAL), [hoy]);
  if (datos === undefined) return null;
  const { dias, resumen } = datos;
  // Los días sin nada que decir no llenan la lista
  const conAlgo = dias.filter((d) => d.estado.tipo !== 'sin_ayuno');
  const pendientes = conAlgo.filter((d) => d.estado.tipo === 'por_confirmar' || d.estado.tipo === 'cambio');

  return (
    <div>
      <PageHeader
        titulo="Tus ayunos."
        descripcion={`De los últimos ${DIAS_DEL_HISTORIAL} días, calculados con tus comidas. Solo lo que confirmas cuenta en los promedios y en la racha.`}
        volver={{ a: '/comida', texto: 'Comida', etiqueta: 'Volver a Comida' }}
      />

      <Card>
        <span className="antetitulo">Racha</span>
        <p className="mt-2 text-[34px] leading-none tracking-[-1.2px] text-texto">{textoRacha(resumen.racha)}</p>
        <p className="mt-2 text-[13px] leading-relaxed text-texto-suave">
          Días seguidos cumpliendo tu meta, hasta ayer y también hoy si ya se cumplió. Un día sin meta
          no cuenta ni la corta.
        </p>
        <dl className="mt-4 grid grid-cols-3 gap-3 border-t border-borde pt-4">
          <div className="min-w-0">
            <dt className="text-[12px] text-texto-suave">Últimos 7 días</dt>
            <dd className="mt-1">
              <span className="block text-[17px] tabular-nums text-texto">{horasOGuion(resumen.ultimos7.horas)}</span>
              <span className="block text-[12px] text-texto-suave">
                {resumen.ultimos7.dias === 1 ? '1 ayuno' : `${resumen.ultimos7.dias} ayunos`}
              </span>
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[12px] text-texto-suave">Últimos 30</dt>
            <dd className="mt-1">
              <span className="block text-[17px] tabular-nums text-texto">{horasOGuion(resumen.ultimos30.horas)}</span>
              <span className="block text-[12px] text-texto-suave">
                {resumen.ultimos30.dias === 1 ? '1 ayuno' : `${resumen.ultimos30.dias} ayunos`}
              </span>
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[12px] text-texto-suave">Este mes</dt>
            <dd className="mt-1">
              <span className="block text-[17px] tabular-nums text-texto">
                {resumen.conMetaDelMes === 0 ? '—' : `${resumen.cumplidosDelMes} de ${resumen.conMetaDelMes}`}
              </span>
              <span className="block text-[12px] text-texto-suave">{resumen.conMetaDelMes === 0 ? 'Sin meta' : 'cumplidos'}</span>
            </dd>
          </div>
        </dl>
      </Card>

      {resumen.porConfirmar > 0 && (
        <div className="mt-4">
          <Banner
            tono="info"
            titulo={
              resumen.porConfirmar === 1
                ? '1 ayuno espera que lo confirmes.'
                : `${resumen.porConfirmar} ayunos esperan que los confirmes.`
            }
          >
            Mientras no los confirmes no entran en los promedios ni en la racha.
          </Banner>
        </div>
      )}

      {conAlgo.length === 0 ? (
        <div className="mt-6">
          <EstadoVacio
            icono={Hourglass}
            titulo="Todavía no hay ayunos."
            descripcion="Se calculan con tus comidas: en cuanto registres una cena y el desayuno del día siguiente, aquí aparecerá el primero."
          />
        </div>
      ) : (
        <div className="mt-8 space-y-6">
          {pendientes.length > 0 && (
            <Seccion titulo="Por revisar" descripcion="Confírmalos o corrige sus horas para que cuenten.">
              <div className="space-y-4">
                {pendientes.map((d) => (
                  <DiaDelHistorial key={d.fecha} dia={d} conAcciones />
                ))}
              </div>
            </Seccion>
          )}
          <Seccion titulo="Día por día" extra={conAlgo.length === 1 ? '1 día' : `${conAlgo.length} días`}>
            <div className="space-y-4">
              {conAlgo.map((d) => (
                <DiaDelHistorial key={d.fecha} dia={d} conAcciones={!pendientes.includes(d)} />
              ))}
            </div>
          </Seccion>
        </div>
      )}
    </div>
  );
}

/** Un día del historial, con su meta y lo que se puede hacer con él. */
function DiaDelHistorial({ dia, conAcciones }: { dia: DiaDeAyuno; conAcciones: boolean }) {
  const etiqueta = fechaCorta(dia.fecha).toLocaleLowerCase('es-MX');
  return (
    <article aria-labelledby={`ayuno-${dia.fecha}`} className="space-y-2 rounded-panel bg-superficie p-4">
      <div className="flex items-baseline justify-between gap-2.5">
        <h3 id={`ayuno-${dia.fecha}`} className="text-[14px] font-medium text-texto">
          {fechaCorta(dia.fecha)}
        </h3>
        <span className="whitespace-nowrap text-[12px] text-texto-suave">
          {dia.metaHoras === null ? 'Sin meta' : `Meta: ${formatearNumero(dia.metaHoras, { decimales: 1, fijos: false })} h`}
        </span>
      </div>
      <DetalleDeAyuno estado={dia.estado} />
      {conAcciones && <AccionesDeAyuno dia={dia.fecha} estado={dia.estado} etiquetaDia={etiqueta} />}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Temporizador y aviso de ayuno largo (Req. 23.4, 23.9; 0.6.2)
// ---------------------------------------------------------------------------

/**
 * El temporizador (Req. 23.4): empezar, terminar y "Empecé antes" las veces
 * que haga falta. Solo con el método de temporizador; con el de comidas el
 * ayuno sale de los registros.
 */
export function ControlesDelTemporizador({ abierto, hoy }: { abierto: Fast | null; hoy: FechaLocal }) {
  const [corrigiendo, setCorrigiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function hacer(fn: () => Promise<unknown>) {
    if (guardando) return;
    setError(null);
    setGuardando(true);
    try {
      await fn();
      setCorrigiendo(false);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <>
      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo">
          {error}
        </Banner>
      )}
      {abierto === null ? (
        <Button icono={Hourglass} disabled={guardando} onClick={() => void hacer(() => iniciarAyuno())}>
          {guardando ? 'Empezando…' : 'Empezar ayuno ahora'}
        </Button>
      ) : corrigiendo ? (
        <FormularioEmpeceAntes
          inicio={abierto.inicio}
          hoy={hoy}
          guardando={guardando}
          onGuardar={(inicioDe) => void hacer(() => corregirInicioEnCurso(inicioDe))}
          onCancelar={() => setCorrigiendo(false)}
        />
      ) : (
        <div className="grid gap-2">
          <Button icono={Check} disabled={guardando} onClick={() => void hacer(() => terminarAyuno())}>
            {guardando ? 'Guardando…' : 'Terminar ayuno'}
          </Button>
          <Button variante="secundario" onClick={() => setCorrigiendo(true)}>
            Empecé antes
          </Button>
        </div>
      )}
    </>
  );
}

/** Corrige la hora de inicio del ayuno en curso, sin terminarlo. */
function FormularioEmpeceAntes({
  inicio,
  hoy,
  guardando,
  onGuardar,
  onCancelar,
}: {
  inicio: string;
  hoy: FechaLocal;
  guardando: boolean;
  onGuardar: (inicioDe: { fecha: FechaLocal; hora: HoraLocal }) => void;
  onCancelar: () => void;
}) {
  const ayer = sumarDias(hoy, -1);
  const [empezoAyer, setEmpezoAyer] = useState(fechaLocalDe(inicio) === ayer);
  const [hora, setHora] = useState(horaLocalDe(inicio));

  return (
    <div className="space-y-4 border-t border-borde pt-3">
      <Segmentado
        etiqueta="Empecé"
        etiquetaVisible
        completo
        valor={empezoAyer ? 'ayer' : 'hoy'}
        opciones={[
          { valor: 'ayer', etiqueta: 'Ayer' },
          { valor: 'hoy', etiqueta: 'Hoy' },
        ]}
        onCambio={(v) => setEmpezoAyer(v === 'ayer')}
      />
      <CampoHora etiqueta="A las" valor={hora} onCambio={setHora} ayuda="Se puede corregir las veces que haga falta." />
      <div className="grid grid-cols-2 gap-2">
        <Button disabled={guardando} onClick={() => onGuardar({ fecha: empezoAyer ? ayer : hoy, hora: hora as HoraLocal })}>
          {guardando ? 'Guardando…' : 'Guardar el inicio'}
        </Button>
        <Button variante="secundario" onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

/**
 * Cerrar la ventana de alimentación (Req. 23.14): desde aquí empieza el ayuno
 * siguiente, contado desde la última ingesta y no desde el momento del toque.
 * Volver a comer después la vuelve a abrir sola, así que no hay nada que
 * deshacer si se cierra antes de tiempo.
 */
export function CerrarVentana({ ventana }: { ventana: VentanaDeAlimentacion }) {
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const desde = formatearHoraDeInstante(ventana.ultimaIngesta);

  async function cerrar() {
    if (guardando) return;
    setError(null);
    setGuardando(true);
    try {
      await cerrarVentanaDeAlimentacion();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <>
      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo">
          {error}
        </Banner>
      )}
      <Button variante="secundario" icono={Hourglass} disabled={guardando} onClick={() => void cerrar()}>
        {guardando ? 'Empezando tu ayuno…' : 'Cerrar mi ventana y empezar a ayunar'}
      </Button>
      <p className="text-[12px] leading-relaxed text-texto-suave">
        Tu ayuno contará desde las {desde}, tu última ingesta. Si vuelves a comer, la ventana se
        abre otra vez sola.
      </p>
    </>
  );
}

/**
 * Un ayuno de más de 24 h se dice una sola vez (Req. 23.9), sin felicitar:
 * puede ser que falte registrar una comida.
 */
export function AvisoAyunoLargo({ aviso }: { aviso: AvisoDeAyunoLargo }) {
  const [guardando, setGuardando] = useState(false);
  return (
    <Banner
      tono="aviso"
      titulo="Tu ayuno pasó de 24 horas."
      accion={
        <Button
          variante="secundario"
          disabled={guardando}
          onClick={() => {
            setGuardando(true);
            void marcarAvisoLargoVisto(aviso.clave).finally(() => setGuardando(false));
          }}
        >
          Entendido
        </Button>
      }
    >
      {textoAvisoLargo(aviso)}
    </Banner>
  );
}

// ---------------------------------------------------------------------------
// Método del ayuno (Req. 23.1), en Ajustes
// ---------------------------------------------------------------------------

export function MetodoDeAyuno({ metodo }: { metodo: MetodoAyuno }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <Seccion titulo="Cómo llevas tu ayuno" descripcion="Se puede cambiar cuando quieras; lo que ya registraste no se toca.">
      <div className="space-y-2">
        <Opciones
          etiqueta="Método del ayuno"
          valor={metodo}
          opciones={[
            { valor: 'comidas', etiqueta: 'Con mis comidas' },
            { valor: 'temporizador', etiqueta: 'Con temporizador' },
          ]}
          onCambio={(v) => {
            setError(null);
            void elegirMetodoDeAyuno(v).catch((e: unknown) => setError(mensajeDeError(e)));
          }}
        />
        <p className="text-[12px] leading-relaxed text-texto-suave">
          {metodo === 'comidas'
            ? 'Con tus comidas: el ayuno se calcula del último alimento de un día al primero del siguiente, y tú lo confirmas. No hay que acordarse de iniciar nada.'
            : 'Con temporizador: lo empiezas y lo terminas tú, y puedes corregir la hora de inicio con "Empecé antes". Un ayuno así manda sobre el calculado con tus comidas.'}
        </p>
        {error !== null && (
          <Banner tono="alerta" titulo="No se pudo">
            {error}
          </Banner>
        )}
      </div>
    </Seccion>
  );
}
